// src/pages/finance-portal/recurring-invoices/RecurringInvoiceForm.js

import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
    Box, Typography, Paper, Button, TextField, Card, CardContent,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    IconButton, Alert, CircularProgress, Autocomplete,
    MenuItem, Select, FormControl, InputLabel, Divider,
    Checkbox, FormControlLabel,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import { useNavigate, useParams } from 'react-router-dom';
import axiosInstance from '../../../utils/axiosInstance';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";
const ACCENT = '#22c55e';

const PAYMENT_TERMS = [
    { value: 'DUE_ON_RECEIPT', label: 'Due on Receipt' },
    { value: 'NET_15', label: 'Net 15' },
    { value: 'NET_30', label: 'Net 30' },
    { value: 'NET_60', label: 'Net 60' },
    { value: 'NET_90', label: 'Net 90' },
];

const ITEM_TYPES = [
    { value: 'SERVICE', label: 'Service' },
    { value: 'PRODUCT', label: 'Product' },
    { value: 'EXPENSE', label: 'Expense' },
    { value: 'DISCOUNT', label: 'Discount' },
];

const FREQUENCIES = [
    { value: 'DAILY', label: 'Daily' },
    { value: 'WEEKLY', label: 'Weekly' },
    { value: 'MONTHLY', label: 'Monthly' },
    { value: 'YEARLY', label: 'Yearly' },
];

const ESCALATION_FREQUENCIES = [
    { value: 'NONE', label: 'None' },
    { value: 'ANNUAL', label: 'Annual' },
    { value: 'SEMI_ANNUAL', label: 'Semi-Annual' },
];

const DAY_OF_WEEK_OPTIONS = [
    { value: 'MO', label: 'Monday' },
    { value: 'TU', label: 'Tuesday' },
    { value: 'WE', label: 'Wednesday' },
    { value: 'TH', label: 'Thursday' },
    { value: 'FR', label: 'Friday' },
    { value: 'SA', label: 'Saturday' },
    { value: 'SU', label: 'Sunday' },
];

function formatCurrency(val) {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(val || 0);
}

const emptyLine = {
    item_type: 'SERVICE',
    description: '',
    quantity: 1,
    unit_price: 0,
    display_order: 0,
};

function buildRrule(frequency, interval, byMonthDay, byDay, byMonth) {
    let parts = [`FREQ=${frequency}`];
    if (interval && interval > 1) {
        parts.push(`INTERVAL=${interval}`);
    }
    if (frequency === 'MONTHLY' && byMonthDay) {
        parts.push(`BYMONTHDAY=${byMonthDay}`);
    }
    if (frequency === 'WEEKLY' && byDay) {
        parts.push(`BYDAY=${byDay}`);
    }
    if (frequency === 'YEARLY' && byMonth) {
        parts.push(`BYMONTH=${byMonth}`);
        if (byMonthDay) {
            parts.push(`BYMONTHDAY=${byMonthDay}`);
        }
    }
    return parts.join(';');
}

function parseRrule(rrule) {
    const result = { frequency: 'MONTHLY', interval: 1, byMonthDay: '', byDay: '', byMonth: '' };
    if (!rrule) return result;
    rrule.split(';').forEach(part => {
        const [key, val] = part.split('=');
        if (!key || !val) return;
        switch (key.toUpperCase()) {
            case 'FREQ': result.frequency = val; break;
            case 'INTERVAL': result.interval = parseInt(val) || 1; break;
            case 'BYMONTHDAY': result.byMonthDay = val; break;
            case 'BYDAY': result.byDay = val; break;
            case 'BYMONTH': result.byMonth = val; break;
            default: break;
        }
    });
    return result;
}

export default function RecurringInvoiceForm() {
    const navigate = useNavigate();
    const { recurringId } = useParams();
    const isEdit = Boolean(recurringId);

    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);

    // Customer
    const [customerOptions, setCustomerOptions] = useState([]);
    const [customerSearch, setCustomerSearch] = useState('');
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [customerLoading, setCustomerLoading] = useState(false);
    const customerTimer = useRef(null);

    // Basic fields
    const [name, setName] = useState('');
    const [paymentTerms, setPaymentTerms] = useState('NET_30');
    const [currency, setCurrency] = useState('USD');
    const [autoSend, setAutoSend] = useState(false);

    // RRULE fields
    const [frequency, setFrequency] = useState('MONTHLY');
    const [interval, setInterval] = useState(1);
    const [byMonthDay, setByMonthDay] = useState('1');
    const [byDay, setByDay] = useState('MO');
    const [byMonth, setByMonth] = useState('1');

    // Dates
    const [startDate, setStartDate] = useState(new Date().toISOString().split('T')[0]);
    const [endDate, setEndDate] = useState('');

    // Price Escalation
    const [escalationPercent, setEscalationPercent] = useState('');
    const [escalationFrequency, setEscalationFrequency] = useState('NONE');

    // Line items
    const [lines, setLines] = useState([{ ...emptyLine, display_order: 1 }]);

    // Generated RRULE preview
    const rrulePreview = buildRrule(frequency, interval, byMonthDay, byDay, byMonth);

    // Customer search with debounce
    useEffect(() => {
        if (customerTimer.current) clearTimeout(customerTimer.current);
        const params = { per_page: 50 };
        if (customerSearch && customerSearch.length >= 2) params.search = customerSearch;
        setCustomerLoading(true);
        customerTimer.current = setTimeout(async () => {
            try {
                const res = await axiosInstance.get('/api/finance/customers', { params });
                if (res.data.success) {
                    setCustomerOptions(res.data.data || []);
                }
            } catch (err) {
                console.error('Failed to search customers', err);
            } finally {
                setCustomerLoading(false);
            }
        }, customerSearch ? 300 : 0);
        return () => { if (customerTimer.current) clearTimeout(customerTimer.current); };
    }, [customerSearch]);

    // Load existing recurring invoice
    useEffect(() => {
        if (isEdit) {
            setLoading(true);
            axiosInstance.get(`/api/finance/recurring-invoices/${recurringId}`)
                .then(res => {
                    if (res.data.success) {
                        const item = res.data.data;
                        setName(item.name || '');
                        setPaymentTerms(item.payment_terms || 'NET_30');
                        setCurrency(item.currency || 'USD');
                        setAutoSend(item.auto_send || false);
                        setStartDate(item.start_date || '');
                        setEndDate(item.end_date || '');
                        setEscalationPercent(item.escalation_percent != null ? String(item.escalation_percent) : '');
                        setEscalationFrequency(item.escalation_frequency || 'NONE');

                        if (item.customer) {
                            setSelectedCustomer(item.customer);
                            setCustomerOptions([item.customer]);
                        }

                        // Parse RRULE
                        const parsed = parseRrule(item.rrule);
                        setFrequency(parsed.frequency);
                        setInterval(parsed.interval);
                        setByMonthDay(parsed.byMonthDay || '1');
                        setByDay(parsed.byDay || 'MO');
                        setByMonth(parsed.byMonth || '1');

                        if (item.line_items && item.line_items.length > 0) {
                            setLines(item.line_items.map((li, idx) => ({
                                id: li.id,
                                item_type: li.item_type || 'SERVICE',
                                description: li.description || '',
                                quantity: li.quantity != null ? li.quantity : 1,
                                unit_price: li.unit_price != null ? li.unit_price : 0,
                                display_order: li.display_order != null ? li.display_order : idx + 1,
                            })));
                        }
                    }
                })
                .catch(err => {
                    setError(err.response?.data?.message || 'Failed to load recurring invoice');
                })
                .finally(() => setLoading(false));
        }
    }, [isEdit, recurringId]);

    const updateLine = (index, field, value) => {
        const updated = [...lines];
        updated[index] = { ...updated[index], [field]: value };
        setLines(updated);
    };

    const addLine = () => {
        const maxOrder = lines.reduce((max, l) => Math.max(max, l.display_order || 0), 0);
        setLines([...lines, { ...emptyLine, display_order: maxOrder + 1 }]);
    };

    const removeLine = (index) => {
        if (lines.length > 1) {
            setLines(lines.filter((_, i) => i !== index));
        }
    };

    const lineAmount = (line) => (parseFloat(line.quantity) || 0) * (parseFloat(line.unit_price) || 0);
    const subtotal = lines.reduce((sum, l) => sum + lineAmount(l), 0);

    const handleSave = async () => {
        if (!name.trim()) {
            setError('Name is required');
            return;
        }
        if (!selectedCustomer) {
            setError('Customer is required');
            return;
        }

        setSaving(true);
        setError(null);
        setSuccess(null);

        const payload = {
            name: name.trim(),
            customer_id: selectedCustomer.id,
            rrule: rrulePreview,
            start_date: startDate,
            end_date: endDate || null,
            payment_terms: paymentTerms,
            currency: currency,
            auto_send: autoSend,
            escalation_percent: parseFloat(escalationPercent) || 0,
            escalation_frequency: escalationFrequency,
            line_items: lines.map((l, idx) => ({
                id: l.id || undefined,
                item_type: l.item_type,
                description: l.description,
                quantity: parseFloat(l.quantity) || 0,
                unit_price: parseFloat(l.unit_price) || 0,
                display_order: idx + 1,
            })),
        };

        try {
            if (isEdit) {
                await axiosInstance.put(`/api/finance/recurring-invoices/${recurringId}`, payload);
                setSuccess('Recurring invoice updated successfully');
            } else {
                const res = await axiosInstance.post('/api/finance/recurring-invoices', payload);
                if (res.data.success && res.data.data?.id) {
                    navigate(`/finance-portal/recurring-invoices/${res.data.data.id}/edit`, { replace: true });
                    return;
                }
                setSuccess('Recurring invoice created successfully');
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to save recurring invoice');
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <CircularProgress sx={{ color: ACCENT }} />
            </Box>
        );
    }

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc' }}>
            {/* Header */}
            <Paper elevation={0} sx={{
                background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
                borderRadius: { xs: 0, md: '0 0 24px 24px' },
                px: { xs: 2.5, md: 4 }, py: { xs: 3, md: 4 }, mb: 3,
                position: 'relative', overflow: 'hidden',
            }}>
                <Box sx={{ position: 'absolute', top: -30, right: -30, width: 160, height: 160, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.06)' }} />
                <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <IconButton onClick={() => navigate('/finance-portal/recurring-invoices')} sx={{ color: 'white', mr: 0.5 }}>
                        <ArrowBackIcon />
                    </IconButton>
                    <Box sx={{ p: 1.5, borderRadius: '14px', bgcolor: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <AutorenewIcon sx={{ fontSize: 28, color: '#fff' }} />
                    </Box>
                    <Box>
                        <Typography variant="h4" sx={{ fontFamily, fontWeight: 800, color: 'white', fontSize: { xs: '1.4rem', md: '1.85rem' } }}>
                            {isEdit ? 'Edit Recurring Invoice' : 'New Recurring Invoice'}
                        </Typography>
                        <Typography sx={{ fontFamily, color: 'rgba(255,255,255,0.8)', fontSize: '0.9rem', mt: 0.25 }}>
                            {isEdit ? 'Update recurring invoice settings' : 'Set up automated billing'}
                        </Typography>
                    </Box>
                </Box>
            </Paper>

            <Box sx={{ px: { xs: 2, md: 4 }, pb: 4, maxWidth: 1200, mx: 'auto' }}>
                {error && <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2, borderRadius: '12px', fontFamily }}>{error}</Alert>}
                {success && <Alert severity="success" onClose={() => setSuccess(null)} sx={{ mb: 2, borderRadius: '12px', fontFamily }}>{success}</Alert>}

                {/* Basic Details */}
                <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none', mb: 3 }}>
                    <CardContent sx={{ p: 3 }}>
                        <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '1rem', color: '#1e293b', mb: 2 }}>Basic Details</Typography>

                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, mb: 2 }}>
                            <TextField
                                size="small" label="Name" required
                                value={name}
                                onChange={e => setName(e.target.value)}
                                placeholder="e.g., Monthly Hosting Fee"
                                InputLabelProps={{ sx: { fontFamily } }}
                                InputProps={{ sx: { fontFamily, borderRadius: '10px' } }}
                            />
                            <Autocomplete
                                size="small"
                                options={customerOptions}
                                getOptionLabel={opt => opt ? (opt.company_name || `${opt.first_name || ''} ${opt.last_name || ''}`.trim()) : ''}
                                value={selectedCustomer}
                                onChange={(_, val) => setSelectedCustomer(val)}
                                onInputChange={(_, val) => setCustomerSearch(val)}
                                loading={customerLoading}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Customer"
                                        required
                                        InputLabelProps={{ ...params.InputLabelProps, sx: { fontFamily } }}
                                        InputProps={{
                                            ...params.InputProps,
                                            sx: { fontFamily, borderRadius: '10px' },
                                            endAdornment: (
                                                <>
                                                    {customerLoading ? <CircularProgress color="inherit" size={16} /> : null}
                                                    {params.InputProps.endAdornment}
                                                </>
                                            ),
                                        }}
                                    />
                                )}
                                isOptionEqualToValue={(option, value) => option.id === value.id}
                                noOptionsText="Type to search customers"
                            />
                        </Box>

                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr' }, gap: 2 }}>
                            <FormControl size="small">
                                <InputLabel sx={{ fontFamily }}>Payment Terms</InputLabel>
                                <Select
                                    value={paymentTerms}
                                    onChange={e => setPaymentTerms(e.target.value)}
                                    label="Payment Terms"
                                    sx={{ fontFamily, borderRadius: '10px' }}
                                >
                                    {PAYMENT_TERMS.map(pt => (
                                        <MenuItem key={pt.value} value={pt.value} sx={{ fontFamily }}>{pt.label}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <TextField
                                size="small" label="Currency"
                                value={currency}
                                onChange={e => setCurrency(e.target.value)}
                                InputLabelProps={{ sx: { fontFamily } }}
                                InputProps={{ sx: { fontFamily, borderRadius: '10px' } }}
                            />
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={autoSend}
                                            onChange={e => setAutoSend(e.target.checked)}
                                            sx={{ color: '#94a3b8', '&.Mui-checked': { color: ACCENT } }}
                                        />
                                    }
                                    label={<Typography sx={{ fontFamily, fontSize: '0.85rem' }}>Auto-send invoices</Typography>}
                                />
                            </Box>
                        </Box>
                    </CardContent>
                </Card>

                {/* Schedule (RRULE Builder) */}
                <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none', mb: 3 }}>
                    <CardContent sx={{ p: 3 }}>
                        <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '1rem', color: '#1e293b', mb: 2 }}>Schedule</Typography>

                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr' }, gap: 2, mb: 2 }}>
                            <FormControl size="small">
                                <InputLabel sx={{ fontFamily }}>Frequency</InputLabel>
                                <Select
                                    value={frequency}
                                    onChange={e => setFrequency(e.target.value)}
                                    label="Frequency"
                                    sx={{ fontFamily, borderRadius: '10px' }}
                                >
                                    {FREQUENCIES.map(f => (
                                        <MenuItem key={f.value} value={f.value} sx={{ fontFamily }}>{f.label}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <TextField
                                size="small" label="Interval" type="number"
                                value={interval}
                                onChange={e => setInterval(parseInt(e.target.value) || 1)}
                                inputProps={{ min: 1, max: 365 }}
                                InputLabelProps={{ sx: { fontFamily } }}
                                InputProps={{ sx: { fontFamily, borderRadius: '10px' } }}
                                helperText={`Every ${interval} ${frequency.toLowerCase()}${interval > 1 ? 's' : ''}`}
                                FormHelperTextProps={{ sx: { fontFamily, fontSize: '0.7rem' } }}
                            />
                            {frequency === 'WEEKLY' && (
                                <FormControl size="small">
                                    <InputLabel sx={{ fontFamily }}>Day of Week</InputLabel>
                                    <Select
                                        value={byDay}
                                        onChange={e => setByDay(e.target.value)}
                                        label="Day of Week"
                                        sx={{ fontFamily, borderRadius: '10px' }}
                                    >
                                        {DAY_OF_WEEK_OPTIONS.map(d => (
                                            <MenuItem key={d.value} value={d.value} sx={{ fontFamily }}>{d.label}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            )}
                            {frequency === 'MONTHLY' && (
                                <TextField
                                    size="small" label="Day of Month" type="number"
                                    value={byMonthDay}
                                    onChange={e => setByMonthDay(e.target.value)}
                                    inputProps={{ min: 1, max: 31 }}
                                    InputLabelProps={{ sx: { fontFamily } }}
                                    InputProps={{ sx: { fontFamily, borderRadius: '10px' } }}
                                />
                            )}
                            {frequency === 'YEARLY' && (
                                <>
                                    <FormControl size="small">
                                        <InputLabel sx={{ fontFamily }}>Month</InputLabel>
                                        <Select
                                            value={byMonth}
                                            onChange={e => setByMonth(e.target.value)}
                                            label="Month"
                                            sx={{ fontFamily, borderRadius: '10px' }}
                                        >
                                            {['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'].map((m, i) => (
                                                <MenuItem key={i + 1} value={String(i + 1)} sx={{ fontFamily }}>{m}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                    <TextField
                                        size="small" label="Day of Month" type="number"
                                        value={byMonthDay}
                                        onChange={e => setByMonthDay(e.target.value)}
                                        inputProps={{ min: 1, max: 31 }}
                                        InputLabelProps={{ sx: { fontFamily } }}
                                        InputProps={{ sx: { fontFamily, borderRadius: '10px' } }}
                                    />
                                </>
                            )}
                        </Box>

                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, mb: 2 }}>
                            <TextField
                                size="small" type="date" label="Start Date" required
                                value={startDate}
                                onChange={e => setStartDate(e.target.value)}
                                InputLabelProps={{ shrink: true, sx: { fontFamily } }}
                                InputProps={{ sx: { fontFamily, borderRadius: '10px' } }}
                            />
                            <TextField
                                size="small" type="date" label="End Date (Optional)"
                                value={endDate}
                                onChange={e => setEndDate(e.target.value)}
                                InputLabelProps={{ shrink: true, sx: { fontFamily } }}
                                InputProps={{ sx: { fontFamily, borderRadius: '10px' } }}
                            />
                        </Box>

                        {/* RRULE Preview */}
                        <Box sx={{
                            p: 1.5, borderRadius: '10px', bgcolor: '#f0fdf4', border: '1px solid #bbf7d0',
                            display: 'flex', alignItems: 'center', gap: 1,
                        }}>
                            <AutorenewIcon sx={{ fontSize: 18, color: ACCENT }} />
                            <Typography sx={{ fontFamily, fontSize: '0.8rem', color: '#166534', fontWeight: 600 }}>
                                RRULE: {rrulePreview}
                            </Typography>
                        </Box>
                    </CardContent>
                </Card>

                {/* Price Escalation */}
                <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none', mb: 3 }}>
                    <CardContent sx={{ p: 3 }}>
                        <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '1rem', color: '#1e293b', mb: 2 }}>Price Escalation</Typography>
                        <Typography sx={{ fontFamily, fontSize: '0.85rem', color: '#64748b', mb: 2 }}>
                            Automatically increase prices on a schedule.
                        </Typography>
                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                            <TextField
                                size="small" label="Escalation Percent (%)" type="number"
                                value={escalationPercent}
                                onChange={e => setEscalationPercent(e.target.value)}
                                inputProps={{ min: 0, max: 100, step: '0.01' }}
                                InputLabelProps={{ sx: { fontFamily } }}
                                InputProps={{ sx: { fontFamily, borderRadius: '10px' } }}
                                placeholder="e.g., 3"
                            />
                            <FormControl size="small">
                                <InputLabel sx={{ fontFamily }}>Escalation Frequency</InputLabel>
                                <Select
                                    value={escalationFrequency}
                                    onChange={e => setEscalationFrequency(e.target.value)}
                                    label="Escalation Frequency"
                                    sx={{ fontFamily, borderRadius: '10px' }}
                                >
                                    {ESCALATION_FREQUENCIES.map(ef => (
                                        <MenuItem key={ef.value} value={ef.value} sx={{ fontFamily }}>{ef.label}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Box>
                    </CardContent>
                </Card>

                {/* Line Items */}
                <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none', mb: 3 }}>
                    <CardContent sx={{ p: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                            <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '1rem', color: '#1e293b' }}>Line Items</Typography>
                            <Button
                                size="small"
                                startIcon={<AddIcon />}
                                onClick={addLine}
                                sx={{ fontFamily, textTransform: 'none', color: ACCENT, fontWeight: 600 }}
                            >
                                Add Line Item
                            </Button>
                        </Box>

                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow sx={{ bgcolor: '#f8fafc' }}>
                                        <TableCell sx={{ fontFamily, fontWeight: 700, fontSize: '0.7rem', color: '#64748b', textTransform: 'uppercase', width: 110 }}>Type</TableCell>
                                        <TableCell sx={{ fontFamily, fontWeight: 700, fontSize: '0.7rem', color: '#64748b', textTransform: 'uppercase' }}>Description</TableCell>
                                        <TableCell sx={{ fontFamily, fontWeight: 700, fontSize: '0.7rem', color: '#64748b', textTransform: 'uppercase', width: 80 }}>Qty</TableCell>
                                        <TableCell sx={{ fontFamily, fontWeight: 700, fontSize: '0.7rem', color: '#64748b', textTransform: 'uppercase', width: 110 }}>Unit Price</TableCell>
                                        <TableCell align="right" sx={{ fontFamily, fontWeight: 700, fontSize: '0.7rem', color: '#64748b', textTransform: 'uppercase', width: 110 }}>Amount</TableCell>
                                        <TableCell sx={{ width: 50 }} />
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {lines.map((line, index) => {
                                        const amt = lineAmount(line);
                                        return (
                                            <TableRow key={index} sx={{ '&:hover': { bgcolor: '#fafafa' } }}>
                                                <TableCell sx={{ verticalAlign: 'top', pt: 1 }}>
                                                    <Select
                                                        size="small"
                                                        value={line.item_type}
                                                        onChange={e => updateLine(index, 'item_type', e.target.value)}
                                                        sx={{ fontFamily, fontSize: '0.8rem', borderRadius: '8px', width: '100%' }}
                                                    >
                                                        {ITEM_TYPES.map(t => (
                                                            <MenuItem key={t.value} value={t.value} sx={{ fontFamily, fontSize: '0.8rem' }}>{t.label}</MenuItem>
                                                        ))}
                                                    </Select>
                                                </TableCell>
                                                <TableCell sx={{ verticalAlign: 'top', pt: 1 }}>
                                                    <TextField
                                                        size="small"
                                                        fullWidth
                                                        multiline
                                                        maxRows={3}
                                                        placeholder="Description"
                                                        value={line.description}
                                                        onChange={e => updateLine(index, 'description', e.target.value)}
                                                        InputProps={{ sx: { fontFamily, fontSize: '0.85rem', borderRadius: '8px' } }}
                                                    />
                                                </TableCell>
                                                <TableCell sx={{ verticalAlign: 'top', pt: 1 }}>
                                                    <TextField
                                                        size="small"
                                                        type="number"
                                                        placeholder="1"
                                                        value={line.quantity}
                                                        onChange={e => updateLine(index, 'quantity', e.target.value)}
                                                        inputProps={{ min: 0, step: 'any' }}
                                                        InputProps={{ sx: { fontFamily, fontSize: '0.85rem', borderRadius: '8px' } }}
                                                        sx={{ width: '100%' }}
                                                    />
                                                </TableCell>
                                                <TableCell sx={{ verticalAlign: 'top', pt: 1 }}>
                                                    <TextField
                                                        size="small"
                                                        type="number"
                                                        placeholder="0.00"
                                                        value={line.unit_price}
                                                        onChange={e => updateLine(index, 'unit_price', e.target.value)}
                                                        inputProps={{ min: 0, step: '0.01' }}
                                                        InputProps={{ sx: { fontFamily, fontSize: '0.85rem', borderRadius: '8px' } }}
                                                        sx={{ width: '100%' }}
                                                    />
                                                </TableCell>
                                                <TableCell align="right" sx={{ verticalAlign: 'top', pt: 1.5 }}>
                                                    <Typography sx={{ fontFamily, fontSize: '0.85rem', fontWeight: 600, color: '#1e293b' }}>
                                                        {formatCurrency(amt)}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell sx={{ verticalAlign: 'top', pt: 1 }}>
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => removeLine(index)}
                                                        disabled={lines.length <= 1}
                                                        sx={{ color: '#94a3b8', '&:hover': { color: '#ef4444' } }}
                                                    >
                                                        <DeleteIcon fontSize="small" />
                                                    </IconButton>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>

                        <Button
                            size="small"
                            startIcon={<AddIcon />}
                            onClick={addLine}
                            sx={{ fontFamily, textTransform: 'none', color: ACCENT, mt: 1.5, fontWeight: 600 }}
                        >
                            Add Line Item
                        </Button>

                        <Divider sx={{ my: 2 }} />

                        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', minWidth: 220 }}>
                                <Typography sx={{ fontFamily, fontSize: '1.05rem', fontWeight: 700, color: '#1e293b' }}>Total</Typography>
                                <Typography sx={{ fontFamily, fontSize: '1.05rem', fontWeight: 700, color: ACCENT }}>{formatCurrency(subtotal)}</Typography>
                            </Box>
                        </Box>
                    </CardContent>
                </Card>

                {/* Action Buttons */}
                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                    <Button
                        onClick={() => navigate('/finance-portal/recurring-invoices')}
                        sx={{ fontFamily, textTransform: 'none', color: '#64748b', fontWeight: 600 }}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                        onClick={handleSave}
                        disabled={saving}
                        sx={{
                            fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '10px',
                            bgcolor: ACCENT, '&:hover': { bgcolor: '#16a34a' },
                        }}
                    >
                        {saving ? 'Saving...' : (isEdit ? 'Update Recurring Invoice' : 'Create Recurring Invoice')}
                    </Button>
                </Box>
            </Box>
        </Box>
    );
}
